import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { KnowledgeBase } from '@/types/supabase';
import { generateEmbedding } from '@/lib/embeddings';

export async function searchKnowledgeBase(kbName: string, query: string, limit = 5) {
  // Step 1: Find the knowledge base by name
  const { data: kbRow, error: kbError } = await supabase
    .from('knowledge_bases') // make sure table name is correct
    .select('id')
    .eq('name', kbName)
    .single();

  if (kbError || !kbRow) {
    throw new Error(`Knowledge base '${kbName}' not found.`);
  }

  // Step 2: Generate query embedding using HF-compatible pipeline
  const queryEmbedding = await generateEmbedding(query);
  if (!queryEmbedding) {
    throw new Error('Failed to generate query embedding.');
  }

  // Step 3: Call Supabase RPC for semantic match
  const { data, error } = await supabase.rpc('match_knowledgebase_documents', {
    query_embedding: queryEmbedding,
    kb_id: kbRow.id,
    match_threshold: 0.78,
    match_count: limit,
  });

  if (error) {
    console.error('[searchKnowledgeBase] Supabase RPC failed:', error);
    throw error;
  }

  return data;
}


export function useKnowledgeBaseCore() {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch all knowledge bases the user has access to
  const fetchKnowledgeBases = useCallback(async () => {
    if (!user) {
      setKnowledgeBases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch knowledge bases created by the user
      const { data: ownedKbs, error: ownedError } = await supabase
        .from('knowledge_bases')
        .select(`*`)
        .eq('created_by', user.id)
        .order('created_at', { ascending: false });

      if (ownedError) throw ownedError;

      // Fetch knowledge bases shared with the user more safely
      const { data: sharedKbs, error: sharedError } = await supabase
        .from('knowledge_base_shares')
        .select(`
          knowledge_bases:knowledge_base_id(*)
        `)
        .eq('user_id', user.id);

      if (sharedError) throw sharedError;
      
      // Combine owned and shared knowledge bases, avoiding duplicates
      const sharedKbsData = sharedKbs
        .map(item => item.knowledge_bases)
        .filter(kb => kb !== null);
      
      // Process the owned knowledge bases
      const processedOwnedKbs = await Promise.all((ownedKbs || []).map(async (kb) => {
        // Count documents for each knowledge base
        const { count: docCount, error: docCountError } = await supabase
          .from('knowledge_base_documents')
          .select('*', { count: 'exact', head: true })
          .eq('knowledge_base_id', kb.id);
          
        if (docCountError) {
          console.error('Error fetching document count:', docCountError);
          return {
            ...kb,
            documents_count: 0,
            documents: [],
            shared_with_count: 0
          };
        }
        
        return {
          ...kb,
          documents_count: docCount || 0,
          documents: [],
          shared_with_count: 0
        };
      }));

      // Process shared knowledge bases
      const processedSharedKbs = await Promise.all((sharedKbsData || []).map(async (kb) => {
        // Count documents for each shared knowledge base
        const { count: docCount, error: docCountError } = await supabase
          .from('knowledge_base_documents')
          .select('*', { count: 'exact', head: true })
          .eq('knowledge_base_id', kb.id);
          
        if (docCountError) {
          console.error('Error fetching document count for shared kb:', docCountError);
          return {
            ...kb,
            documents_count: 0,
            documents: [],
            shared_with_count: 0
          };
        }
        
        return {
          ...kb,
          documents_count: docCount || 0,
          documents: [],
          shared_with_count: 0
        };
      }));
      
      // Combine and filter out duplicates
      const allKbs = [...processedOwnedKbs];
      processedSharedKbs.forEach(sharedKb => {
        if (!allKbs.some(kb => kb.id === sharedKb.id)) {
          allKbs.push(sharedKb);
        }
      });
      
      // Get share counts for each knowledge base
      const kbsWithShares = await Promise.all(allKbs.map(async (kb) => {
        const { count, error: countError } = await supabase
          .from('knowledge_base_shares')
          .select('*', { count: 'exact', head: true })
          .eq('knowledge_base_id', kb.id);
          
        return {
          ...kb,
          shared_with_count: count || 0
        };
      }));
      
      setKnowledgeBases(kbsWithShares);
    } catch (error) {
      console.error('Error fetching knowledge bases:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch knowledge bases',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  // Get a specific knowledge base with its documents
  const getKnowledgeBase = async (kbId: string) => {
    try {
      console.log("Fetching knowledge base with ID:", kbId);
      const { data, error } = await supabase
        .from('knowledge_bases')
        .select('*')
        .eq('id', kbId)
        .single();

      if (error) throw error;
      
      // Fetch documents for this knowledge base
      const { data: documents, error: docError } = await supabase
        .from('knowledge_base_documents')
        .select('*')
        .eq('knowledge_base_id', kbId)
        .order('created_at', { ascending: false });
      
      if (docError) throw docError;

      console.log("Fetched documents:", documents?.length || 0);

      // Fetch share count
      const { count: shareCount, error: shareError } = await supabase
        .from('knowledge_base_shares')
        .select('*', { count: 'exact', head: true })
        .eq('knowledge_base_id', kbId);
      
      if (shareError) throw shareError;
      
      return { 
        ...data, 
        documents: documents || [],
        shared_with_count: shareCount || 0
      };
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch knowledge base',
        variant: 'destructive'
      });
      return null;
    }
  };

  // Get a knowledge base by name
  const getKnowledgeBaseByName = async (name: string) => {
    try {
      const data = await fetchKnowledgeBaseByName(name);
      if (!data) return null;

      // Fetch documents
      const { data: documents, error: docError } = await supabase
        .from('knowledge_base_documents')
        .select('*')
        .eq('knowledge_base_id', data.id)
        .order('created_at', { ascending: false });

      if (docError) throw docError;

      // Share count
      const { count: shareCount, error: shareError } = await supabase
        .from('knowledge_base_shares')
        .select('*', { count: 'exact', head: true })
        .eq('knowledge_base_id', data.id);

      if (shareError) throw shareError;

      return {
        ...data,
        documents: documents || [],
        shared_with_count: shareCount || 0
      };
    } catch (error) {
      console.error('[getKnowledgeBaseByName] Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch knowledge base',
        variant: 'destructive'
      });
      return null;
    }
  };


  // Create a new knowledge base
  const createKnowledgeBase = async (
    name: string, 
    description?: string, 
    organizationId?: string,
    isPrivate: boolean = false
  ) => {
    if (!user) return null;
    
    try {
      // Create the knowledge base without an organization_id to avoid RLS recursion
      // Even if the user provides an organizationId, we'll temporarily avoid using it
      const { data, error } = await supabase
        .from('knowledge_bases')
        .insert({
          name,
          description,
          // Explicitly set organization_id to null to avoid any RLS policies checking org membership
          organization_id: null,
          created_by: user.id,
          is_private: isPrivate
        })
        .select()
        .single();

      if (error) {
        console.error('Error details:', error);
        throw error;
      }

      // If an organization ID was provided and the initial creation was successful,
      // we can update the knowledge base with the organization ID in a separate query
      if (organizationId && data) {
        const { error: updateError } = await supabase
          .from('knowledge_bases')
          .update({ organization_id: organizationId })
          .eq('id', data.id);
          
        if (updateError) {
          console.warn('Could not associate knowledge base with organization:', updateError);
          // Continue anyway since the knowledge base was created successfully
        }
      }
      
      toast({
        title: 'Success',
        description: 'Knowledge base created successfully',
      });
      
      return data.id;
    } catch (error: any) {
      console.error('Error creating knowledge base:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create knowledge base',
        variant: 'destructive'
      });
      return null;
    }
  };

  // Update a knowledge base
  const updateKnowledgeBase = async (
    id: string, 
    name: string, 
    description?: string, 
    isPrivate?: boolean
  ) => {
    if (!user) return false;
    
    try {
      const updates: any = {
        name,
        description,
        updated_at: new Date().toISOString()
      };
      
      if (isPrivate !== undefined) {
        updates.is_private = isPrivate;
      }
      
      const { error } = await supabase
        .from('knowledge_bases')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Knowledge base updated successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error updating knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to update knowledge base',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Delete a knowledge base
  const deleteKnowledgeBase = async (id: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_bases')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Knowledge base deleted successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete knowledge base',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    knowledgeBases,
    loading,
    fetchKnowledgeBases,
    getKnowledgeBase,
    getKnowledgeBaseByName,
    createKnowledgeBase,
    updateKnowledgeBase,
    deleteKnowledgeBase
  };
}